import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { ClockCounterClockwise, Eye, Star, ArrowRight, TrendUp, TrendDown } from "@phosphor-icons/react";
import CompanyLogo from "@/components/CompanyLogo";
import { aaoifiScore, scoreColor } from "@/lib/aaoifi";

const HISTORY_KEY = "screening_history_v1";
const RECENT_VIEW_KEY = "recently_viewed_v1";

function loadHistory() {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]"); } catch { return []; }
}
function loadRecentViewed() {
  try { return JSON.parse(localStorage.getItem(RECENT_VIEW_KEY) || "[]"); } catch { return []; }
}

export function pushRecentlyViewed(stock) {
  if (!stock?.ticker) return;
  try {
    const list = loadRecentViewed().filter((s) => s.ticker !== stock.ticker);
    list.unshift({
      ticker: stock.ticker,
      company_name: stock.company_name,
      verdict: stock.verdict,
      price: stock.price,
      change_percent: stock.change_percent,
      score: aaoifiScore(stock),
      ts: Date.now(),
    });
    localStorage.setItem(RECENT_VIEW_KEY, JSON.stringify(list.slice(0, 12)));
    window.dispatchEvent(new Event("hs:recent-viewed-updated"));
  } catch {
    // ignore quota errors
  }
}

export function pushScreeningHistory(entry) {
  try {
    const list = loadHistory();
    list.unshift({ ...entry, ts: Date.now() });
    localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, 20)));
    window.dispatchEvent(new Event("hs:history-updated"));
  } catch {
    // ignore
  }
}

function verdictColor(v) {
  if (v === "compliant") return "#22c55e";
  if (v === "borderline") return "#f59e0b";
  if (v === "non_compliant") return "#ef4444";
  return "#71717a";
}

function verdictLabel(v) {
  if (v === "compliant") return "Compliant";
  if (v === "borderline") return "Borderline";
  if (v === "non_compliant") return "Non-Compliant";
  return "Unverified";
}

function timeAgo(ts) {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function CardShell({ title, icon: Icon, badge, children, testId, accent = "#60a5fa" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-xl overflow-hidden flex flex-col"
      style={{
        background: "linear-gradient(180deg, #11151D 0%, #0C1017 100%)",
        border: "1px solid rgba(255,255,255,0.06)",
        minHeight: 320,
      }}
      data-testid={testId}
    >
      <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 rounded-md flex items-center justify-center"
            style={{ background: `${accent}14`, border: `1px solid ${accent}22` }}
          >
            <Icon size={12} weight="bold" style={{ color: accent }} />
          </div>
          <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-zinc-300">{title}</span>
        </div>
        {badge != null && (
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded text-zinc-400" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
            {badge}
          </span>
        )}
      </div>
      <div className="flex-1 overflow-auto">{children}</div>
    </motion.div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="flex items-center justify-center h-full min-h-[220px] px-4 text-center">
      <p className="text-[11px] text-zinc-500 leading-relaxed">{label}</p>
    </div>
  );
}

function LogoAvatar({ ticker }) {
  return <CompanyLogo ticker={ticker} size={28} />;
}

function HistoryCard({ onSelect }) {
  const [items, setItems] = useState(loadHistory());
  useEffect(() => {
    const h = () => setItems(loadHistory());
    window.addEventListener("hs:history-updated", h);
    return () => window.removeEventListener("hs:history-updated", h);
  }, []);
  return (
    <CardShell title="Recent Screening History" icon={ClockCounterClockwise} accent="#60a5fa" badge={items.length ? `${items.length}` : null} testId="card-recent-history">
      {items.length === 0 ? (
        <EmptyState label="Filter or search stocks and your screening runs will appear here." />
      ) : (
        <ul className="divide-y" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
          {items.slice(0, 8).map((it, i) => (
            <li key={i}>
              <button
                onClick={() => onSelect?.(it)}
                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.02] transition-colors text-left"
                data-testid={`history-item-${i}`}
              >
                <div className="min-w-0">
                  <div className="text-[12px] text-zinc-200 truncate">{it.label || it.query || "All Stocks"}</div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                    {it.filters ? it.filters : "no filters"} · {it.count ?? 0} results
                  </div>
                </div>
                <span className="text-[10px] font-mono text-zinc-500 shrink-0 ml-3">{timeAgo(it.ts)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}

function RecentlyViewedCard({ onSelect }) {
  const [items, setItems] = useState(loadRecentViewed());
  useEffect(() => {
    const h = () => setItems(loadRecentViewed());
    window.addEventListener("hs:recent-viewed-updated", h);
    return () => window.removeEventListener("hs:recent-viewed-updated", h);
  }, []);
  return (
    <CardShell title="Recently Viewed" icon={Eye} accent="#a78bfa" badge={items.length ? `${items.length}` : null} testId="card-recently-viewed">
      {items.length === 0 ? (
        <EmptyState label="Open any stock from the table below to build your recently-viewed list." />
      ) : (
        <ul className="divide-y" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
          {items.slice(0, 8).map((it, i) => {
            const chg = Number(it.change_percent);
            const positive = chg >= 0;
            return (
              <li key={i}>
                <button
                  onClick={() => onSelect?.(it.ticker)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.02] transition-colors text-left"
                  data-testid={`recent-viewed-${it.ticker}`}
                >
                  <LogoAvatar ticker={it.ticker} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] font-mono font-semibold text-zinc-100">{it.ticker}</span>
                      <span
                        className="text-[9px] font-mono uppercase px-1.5 py-px rounded"
                        style={{ color: verdictColor(it.verdict), background: `${verdictColor(it.verdict)}14`, border: `1px solid ${verdictColor(it.verdict)}22` }}
                      >
                        {verdictLabel(it.verdict)}
                      </span>
                    </div>
                    <div className="text-[10px] text-zinc-500 truncate mt-0.5">{it.company_name}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[12px] font-mono text-zinc-200">
                      {it.score != null ? (
                        <><span style={{ color: scoreColor(it.score) }}>{it.score}</span><span className="text-zinc-500"> / 100</span></>
                      ) : it.price != null ? `$${Number(it.price).toFixed(2)}` : "—"}
                    </div>
                    {Number.isFinite(chg) && (
                      <div className="flex items-center justify-end gap-0.5 text-[10px] font-mono" style={{ color: positive ? "#4ade80" : "#f87171" }}>
                        {positive ? <TrendUp size={9} weight="bold" /> : <TrendDown size={9} weight="bold" />}
                        {positive ? "+" : ""}{chg.toFixed(2)}%
                      </div>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </CardShell>
  );
}

function WatchlistCard({ watchlist, onSelect, onOpenWatchlist }) {
  const tickers = watchlist?.tickers || [];
  return (
    <CardShell title="Watchlist" icon={Star} accent="#f59e0b" badge={tickers.length ? `${tickers.length}` : null} testId="card-watchlist">
      {tickers.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full min-h-[220px] px-4 text-center gap-3">
          <Star size={20} weight="regular" className="text-zinc-600" />
          <p className="text-[11px] text-zinc-500 leading-relaxed max-w-[240px]">
            Save tickers to your Watchlist and monitor their compliance verdicts in one place.
          </p>
        </div>
      ) : (
        <>
          <ul className="divide-y" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
            {tickers.slice(0, 8).map((t) => (
              <li key={t}>
                <button
                  onClick={() => onSelect?.(t)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.02] transition-colors text-left"
                  data-testid={`watchlist-item-${t}`}
                >
                  <LogoAvatar ticker={t} />
                  <span className="text-[12px] font-mono font-semibold text-zinc-100 flex-1">{t}</span>
                  <ArrowRight size={11} weight="bold" className="text-zinc-500" />
                </button>
              </li>
            ))}
          </ul>
          {tickers.length > 8 && (
            <button
              onClick={onOpenWatchlist}
              className="w-full py-2.5 text-[11px] font-mono uppercase tracking-[0.14em] text-zinc-400 hover:text-white border-t transition-colors"
              style={{ borderColor: "rgba(255,255,255,0.05)" }}
              data-testid="watchlist-view-all"
            >
              View all {tickers.length}
            </button>
          )}
        </>
      )}
    </CardShell>
  );
}

export default function DashboardBottomCards({ watchlist, onSelectTicker, onOpenWatchlist }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-6" data-testid="dashboard-bottom-cards">
      <HistoryCard onSelect={(it) => it.query && onSelectTicker?.(it.query)} />
      <RecentlyViewedCard onSelect={onSelectTicker} />
      <WatchlistCard watchlist={watchlist} onSelect={onSelectTicker} onOpenWatchlist={onOpenWatchlist} />
    </div>
  );
}
